import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, ImageIcon, Trash2 } from "lucide-react";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { ImageField } from "@/components/MediaUpload";
import { BrandLogo, HKMonogram } from "@/components/BrandMark";
import { useBrand } from "@/context/BrandContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_KEYS = ["logo", "logo_dark", "logo_mark", "favicon", "logo_height"];

const Slot = ({ title, help, children, testId }) => (
  <div className="hk-card p-5 space-y-3" data-testid={testId}>
    <div>
      <h3 className="hk-h5">{title}</h3>
      <p className="mt-1 text-[12.5px] leading-[1.7] text-[var(--hk-ink-3)]">{help}</p>
    </div>
    {children}
  </div>
);

/** Admin › Logo Management — upload the brand logo once; it replaces the "HerbKranti"
 *  text/monogram across header, footer, popups, auth pages and the admin console. */
export default function AdminLogoPage() {
  const pw = useAdminAuthed();
  const { brand, refresh } = useBrand();
  const [data, setData] = useState(null);     // full `global` settings blob (we only touch logo keys)
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pw) return;
    axios.get(`${API}/admin/settings/global`, { headers: adminHeaders() })
      .then((r) => setData(r.data || {}))
      .catch(() => { toast.error("Failed to load logo settings"); setData({}); });
  }, [pw]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const dirty = data && LOGO_KEYS.some((k) => (data[k] || "") !== (brand[k] || ""));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/global`, data, { headers: adminHeaders() });
      setData(r.data);
      await refresh();
      toast.success("Logo saved — now live across the website");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removeAll = async () => {
    if (!window.confirm("Remove the uploaded logos and go back to the built-in HerbKranti monogram + wordmark?")) return;
    const next = { ...data, logo: "", logo_dark: "", logo_mark: "" };
    setData(next);
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/global`, next, { headers: adminHeaders() });
      setData(r.data);
      await refresh();
      toast.success("Reverted to the default brand mark");
    } catch { toast.error("Could not revert"); } finally { setSaving(false); }
  };

  if (!data) return <AdminLayout title="Logo Management"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;

  const previewHeight = Number(data.logo_height) || 44;
  const PreviewImg = ({ src, dark }) => src
    ? <img src={src} alt="Logo preview" style={{ height: previewHeight, maxWidth: previewHeight * 5 }} className="object-contain object-left" />
    : <BrandLogo variant={dark ? "dark" : "light"} height={previewHeight} showTagline={true} testId={dark ? "logo-preview-dark-fallback" : "logo-preview-light-fallback"} />;

  return (
    <AdminLayout
      title="Logo Management"
      subtitle="Upload your brand logo once — it automatically replaces the HerbKranti name everywhere"
      actions={
        <button onClick={save} disabled={saving || !dirty} className="hk-btn-primary h-10 px-4 inline-flex items-center gap-2 disabled:opacity-50" data-testid="logo-save-btn">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save & apply
        </button>
      }
    >
      <div className="space-y-6" data-testid="admin-logo-page">
        {/* Live preview */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="hk-card p-6 flex flex-col gap-3" data-testid="logo-preview-light">
            <span className="label-eyebrow">Preview · header / light background</span>
            <div className="rounded-lg border border-[var(--hk-border)] bg-[rgba(250,247,242,0.9)] px-6 h-[72px] flex items-center">
              <PreviewImg src={data.logo} />
            </div>
          </div>
          <div className="hk-card p-6 flex flex-col gap-3 !bg-[#141914]" data-testid="logo-preview-dark">
            <span className="label-eyebrow !text-[var(--hk-gold)]">Preview · footer / dark background</span>
            <div className="rounded-lg border border-white/10 bg-[#141914] px-6 h-[72px] flex items-center">
              <PreviewImg src={data.logo_dark || data.logo} dark />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Slot title="Primary logo" testId="logo-slot-primary"
            help="Full logo (image + name) shown in the header, email popup, login pages and admin console. PNG or SVG with transparent background works best. Recommended height ≥ 120px.">
            <ImageField value={data.logo || ""} onChange={(v) => set("logo", v)} label="" aspect="wide" testid="logo-primary" />
          </Slot>
          <Slot title="Logo for dark backgrounds" testId="logo-slot-dark"
            help="Optional light-coloured version used in the footer and other dark sections. If left empty, the primary logo is used there too.">
            <ImageField value={data.logo_dark || ""} onChange={(v) => set("logo_dark", v)} label="" aspect="wide" testid="logo-dark" />
          </Slot>
          <Slot title="Square icon / monogram" testId="logo-slot-mark"
            help="Optional compact square mark used where only an icon fits (founder note, small badges). Falls back to the built-in HK monogram.">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 shrink-0 rounded-lg border border-[var(--hk-border)] bg-white flex items-center justify-center">
                {data.logo_mark ? <img src={data.logo_mark} alt="Mark" className="w-12 h-12 object-contain" /> : <HKMonogram size={44} />}
              </div>
              <div className="flex-1"><ImageField value={data.logo_mark || ""} onChange={(v) => set("logo_mark", v)} label="" testid="logo-mark" /></div>
            </div>
          </Slot>
          <Slot title="Favicon (browser tab icon)" testId="logo-slot-favicon"
            help="Square PNG/ICO, ideally 64×64 or larger. Applied to the browser tab instantly.">
            <ImageField value={data.favicon || ""} onChange={(v) => set("favicon", v)} label="" testid="logo-favicon" />
          </Slot>
        </div>

        <div className="hk-card p-5 flex flex-wrap items-end gap-6" data-testid="logo-options-card">
          <div>
            <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Logo display height (px)</label>
            <input type="number" min="24" max="96" className="hk-input h-10 w-40" value={data.logo_height || 44}
              onChange={(e) => set("logo_height", Math.min(96, Math.max(24, Number(e.target.value) || 44)))} data-testid="logo-height-input" />
            <p className="mt-1 text-[11px] text-[var(--hk-ink-3)]">Header uses this height; footer and admin scale proportionally.</p>
          </div>
          <button onClick={removeAll} disabled={saving || !(data.logo || data.logo_dark || data.logo_mark)}
            className="hk-btn-ghost h-10 px-4 inline-flex items-center gap-2 text-xs disabled:opacity-40 ml-auto" data-testid="logo-remove-btn">
            <Trash2 size={13} /> Remove logos & use default mark
          </button>
        </div>

        <div className="flex items-center gap-2 text-[12px] text-[var(--hk-ink-3)]">
          <ImageIcon size={14} /> Changes go live on the storefront immediately after saving — no deploy needed.
        </div>
      </div>
    </AdminLayout>
  );
}
